import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Home, 
  Building, 
  MessageSquare, 
  Camera, 
  Calendar,
  CheckCircle2
} from 'lucide-react';

const WhoWeHelpSection = () => {
  const audiences = [
    {
      icon: Home,
      title: 'Homeowners',
      description: 'Whether you\'re renovating a single room, extending your home, or refreshing your property, we deliver the finish your home deserves.',
      points: [
        'Kitchen & bathroom renovations',
        'Extensions & loft conversions',
        'Single room re-skims',
        'Crack & damage repairs',
      ],
    },
    {
      icon: Building,
      title: 'Developers & Landlords',
      description: 'Need reliable plastering services? We offer consistent quality, flexible scheduling, and competitive rates.',
      points: [
        'Multi-property projects',
        'Refurbishment',
        'One Off New builds',
        'Insurance Claims',
      ],
    },
  ];

  const processSteps = [
    {
      number: '01',
      icon: MessageSquare,
      title: 'Enquiry',
      description: 'Get in touch via our form or phone. Tell us about your project and what you\'re looking to achieve.',
    },
    {
      number: '02',
      icon: Camera,
      title: 'Quote',
      description: 'We\'ll arrange a site visit or review your photos, then provide a clear, detailed quotation with no hidden costs.',
    },
    {
      number: '03',
      icon: Calendar,
      title: 'Book & Complete',
      description: 'Choose your date, we protect your floors and furniture, complete the work, and leave your space clean and tidy.',
    },
  ];

  return (
    <section 
      id="who-we-help" 
      className="py-20 lg:py-28 bg-slate-50"
      data-testid="who-we-help-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-red-600 font-semibold text-sm tracking-wider uppercase mb-4">
            Who We Work With
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 font-['Manrope'] mb-6">
            Quality Plastering for Every Project
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            From homeowners tackling their first renovation to developers managing 
            multiple sites, we adapt our service to your needs.
          </p>
        </div>

        {/* Audience Cards */}
        <div className="grid lg:grid-cols-2 gap-8 mb-20">
          {audiences.map((audience, index) => (
            <Card 
              key={index}
              className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-300"
              data-testid={`audience-card-${index}`}
            >
              <CardContent className="p-8 lg:p-10">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <audience.icon className="w-7 h-7 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 font-['Manrope']">
                      {audience.title}
                    </h3>
                  </div>
                </div>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {audience.description}
                </p>
                <ul className="space-y-3">
                  {audience.points.map((point, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-slate-700">
                      <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Process Section */}
        <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-sm border border-slate-100">
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Manrope'] mb-4">
              What to Expect
            </h3>
            <p className="text-slate-600 max-w-xl mx-auto">
              A simple, straightforward process from first contact to finished walls.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connector Line (Desktop) */}
            <div className="hidden md:block absolute top-12 left-[20%] right-[20%] h-0.5 bg-red-200" />
            
            {processSteps.map((step, index) => (
              <div 
                key={index}
                className="relative text-center"
                data-testid={`process-step-${index}`}
              >
                <div className="relative inline-flex items-center justify-center mb-6">
                  <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center relative z-10">
                    <step.icon className="w-10 h-10 text-red-600" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-sm font-['Manrope'] z-20">
                    {step.number.slice(1)}
                  </span>
                </div>
                <h4 className="text-xl font-bold text-slate-900 font-['Manrope'] mb-3">
                  {step.title}
                </h4>
                <p className="text-slate-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhoWeHelpSection;
